<aside class="rsc-whatif">

	<div class="rsc-whatif-head">
		<span class="rsc-whatif-title">What if</span>
		<button type="button" class="rsc-whatif-close" (click)="closeWhatIf()" title="Hide">&times;</button>
	</div>

	<select class="rsc-whatif-select" *ngIf="!newArray"
			[(ngModel)]="scaleGroupId" (ngModelChange)="applyMultiplier()">
		<option *ngFor="let o of scaleOptions" [ngValue]="o.id">{{ o.name }}</option>
	</select>

	<!-- Three named sections in the order the question is actually asked: what solar, then what
	     battery, then what that combination is worth. Donal's 17/09/26 note: the answer used to
	     sit between the two inputs, so it read as belonging to the solar alone. -->
	<h4 class="rsc-sec-title">Solar array</h4>

	<!-- What they already have, answered before anything is typed. The same three rows gain an
	     "after" column once a figure goes into the box, so the change reads as a comparison in
	     place rather than as a second list of numbers further down the panel. -->
	<div class="rsc-spec" *ngIf="showArraySpec" [class.rsc-spec-changed]="multiplier !== 1">
		<!-- Empty on purpose: the section heading above says "Solar array", and this cell only
		     exists to hold the Now/After column labels in their columns. -->
		<span class="rsc-spec-title"></span>
		<span class="rsc-spec-col">{{ multiplier !== 1 ? 'Now' : '' }}</span>
		<ng-container *ngIf="multiplier !== 1">
			<span class="rsc-spec-col">After</span>
			<span class="rsc-spec-delta"></span>
		</ng-container>

		<span class="rsc-spec-label">Size (kWp)</span>
		<span class="rsc-spec-now">{{ solarConfig?.installedKwp | number:'1.0-1' }}</span>
		<ng-container *ngIf="multiplier !== 1">
			<span class="rsc-spec-after">{{ totalKwp | number:'1.0-1' }}</span>
			<!-- No delta here on purpose: it is the figure that was just typed into the box. -->
			<span class="rsc-spec-delta"></span>
		</ng-container>

		<ng-container *ngIf="installedPanels !== null">
			<span class="rsc-spec-label">Panels</span>
			<span class="rsc-spec-now">{{ installedPanels | number:'1.0-0' }}</span>
			<ng-container *ngIf="multiplier !== 1">
				<span class="rsc-spec-after">{{ totalPanels | number:'1.0-0' }}</span>
				<span class="rsc-spec-delta">{{ extraPanels ? '+' + (extraPanels | number:'1.0-0') : '' }}</span>
			</ng-container>
		</ng-container>

		<ng-container *ngIf="installedAreaM2 !== null">
			<span class="rsc-spec-label">Area (m²)</span>
			<span class="rsc-spec-now">{{ installedAreaM2 | number:'1.0-0' }}</span>
			<ng-container *ngIf="multiplier !== 1">
				<span class="rsc-spec-after">{{ totalAreaM2 | number:'1.0-0' }}</span>
				<span class="rsc-spec-delta">{{ extraRoofM2 ? '+' + (extraRoofM2 | number:'1.0-0') : '' }}</span>
			</ng-container>
		</ng-container>
	</div>

	<!-- Sized in kWp wherever the site's installed capacity is recorded, because that is the unit
	     every proposal and every quote is written in. The multiple is derived underneath and
	     shown back. A site with no recorded capacity keeps the plain multiple, which is the only
	     thing we can honestly offer there. -->
	<input type="range" class="rsc-whatif-range" *ngIf="canSizeByKwp"
		   min="0" [max]="maxAddKwp" step="10"
		   [(ngModel)]="addKwp" (ngModelChange)="applyMultiplier()" />

	<div class="rsc-whatif-row" *ngIf="canSizeByKwp">
		<input type="number" class="rsc-whatif-number"
			   min="0" step="10"
			   [(ngModel)]="addKwp" (ngModelChange)="applyMultiplier()" />
		<span class="rsc-whatif-x">{{ newArray ? 'kWp of solar' : 'kWp to add' }}</span>
		<span class="rsc-info" tabindex="0"
			  (mouseenter)="showTip($event, tipKwp)" (mouseleave)="hideTip()"
			  (focus)="showTip($event, tipKwp)" (blur)="hideTip()">i</span>
		<button type="button" class="rsc-whatif-reset"
				*ngIf="multiplier !== 1" (click)="resetMultiplier()">Reset</button>
	</div>

	<p class="rsc-whatif-hint" *ngIf="newArray">
		No solar on this chart. Type a size to see what it would generate against this consumption.
	</p>
	<p class="rsc-whatif-hint rsc-solar-source" *ngIf="newArray && solarEstimateNote">{{ solarEstimateNote }}</p>

	<p class="rsc-whatif-hint" *ngIf="canSizeByKwp && !newArray && multiplier !== 1">
		{{ totalKwp | number:'1.0-1' }} kWp in total, {{ multiplier | number:'1.0-2' }}x what is
		generating now.
	</p>

	<input type="range" class="rsc-whatif-range" *ngIf="!canSizeByKwp"
		   min="0.5" max="3" step="0.25"
		   [(ngModel)]="multiplier" (ngModelChange)="applyMultiplier()" />

	<div class="rsc-whatif-row" *ngIf="!canSizeByKwp">
		<input type="number" class="rsc-whatif-number"
			   min="0.5" max="5" step="0.1"
			   [(ngModel)]="multiplier" (ngModelChange)="applyMultiplier()" />
		<span class="rsc-whatif-x">x metered</span>
		<button type="button" class="rsc-whatif-reset"
				*ngIf="multiplier !== 1" (click)="resetMultiplier()">Reset</button>
	</div>

	<!-- Each line only appears when the figure it needs is recorded against the site. -->
	<dl class="rsc-whatif-out" *ngIf="multiplier !== 1">
		<!-- Panel count and area used to be listed here as well. They are in the array summary at
		     the top of the panel now, as a total with the delta beside it, so saying them twice was
		     just length. Note it is "area", never "roof": Lee Strand's new array is going in a field,
		     and a roof figure frames the whole question wrongly on any ground-mount site. Worth per
		     year, solar only, is gone the same way: it is the first term inside the answer above now,
		     and showing a second, smaller euro figure here read as a second opinion rather than a
		     subtotal. -->
		<ng-container *ngIf="extraKwp !== null && !canSizeByKwp">
			<dt>Extra capacity</dt>
			<dd>{{ extraKwp | number:'1.0-1' }} kWp</dd>
		</ng-container>

		<ng-container *ngIf="extraPanels !== null && !canSizeByKwp">
			<dt>Panels to add</dt>
			<dd>{{ extraPanels | number:'1.0-0' }}</dd>
		</ng-container>

		<ng-container *ngIf="extraRoofM2 !== null && !canSizeByKwp">
			<dt>Area needed</dt>
			<dd>{{ extraRoofM2 | number:'1.0-0' }} m²</dd>
		</ng-container>

		<ng-container *ngIf="importAvoidedKwh !== null">
			<dt>Grid avoided</dt>
			<dd>{{ importAvoidedKwh | number:'1.0-0' }} {{ unit }}</dd>
		</ng-container>
	</dl>

	<p class="rsc-whatif-warn" *ngIf="roofShortfallM2 !== null && roofShortfallM2 > 0">
		{{ roofShortfallM2 | number:'1.0-0' }} m² more than the usable space recorded for this site.
	</p>

	<p class="rsc-whatif-warn" *ngIf="clipped > 0">
		{{ clipped | number:'1.0-0' }} {{ unit }} lost to the inverter limit.
	</p>

	<p class="rsc-whatif-note" *ngIf="multiplier !== 1 && !solarConfig && !newArray">
		No solar figures for this site, so this is only a multiple of what was metered.
	</p>

	<!-- Always here, never behind an "Add a battery" button. Donal's 17/09/26 instruction, and he
	     is right: a battery hidden behind a toggle is a feature nobody in the room knows exists.
	     It reads as a section of the page now, the same way the solar array does. -->
	<div class="rsc-batt">
		<h4 class="rsc-sec-title">Battery</h4>

		<!-- Typed in kWh, like the solar in kWp (Jayden, 28/09/26), rather than picked from a list of
		     cabinet sizes and counted. Nothing here is seeded from the site: Donal is specifying the
		     system, not inspecting one. -->
		<div class="rsc-battsize" *ngIf="canShowBattery">
			<div class="rsc-whatif-row">
				<input type="number" class="rsc-whatif-number" id="rscBattKwh"
					   min="0" step="10" aria-label="Battery size, kWh"
					   [(ngModel)]="batteryKwhEntry" (ngModelChange)="onBatteryChange()" />
				<span class="rsc-whatif-x">kWh of storage</span>
			</div>

			<p class="rsc-whatif-hint" *ngIf="batteryKwh !== null && batteryPowerKw !== null">
				Gives back up to {{ batteryPowerKw | number:'1.0-0' }} kW at once.
			</p>

			<p class="rsc-whatif-hint" *ngIf="batteryKwh === null">
				No battery in this scenario. Enter a size above to add one.
			</p>

			<!-- Donal's ask, 18/09/26. Deliberately units and not euro: his words were "it's more
			     usage than anything else", and he prices it himself afterwards because the rates
			     differ per client. The assumption underneath is his too, the battery fills every
			     night, which is why the copy says so rather than hiding it in a tooltip. -->
			<label class="rsc-battcheck" *ngIf="batteryKwh !== null">
				<input type="checkbox"
					   [(ngModel)]="nightCharge" (ngModelChange)="onBatteryChange()" />
				<span>Charge from the grid at night</span>
			</label>

			<p class="rsc-whatif-hint" *ngIf="batteryKwh !== null && nightCharge">
				Fills between {{ nightFromLabel }} and {{ nightToLabel }}, then runs the site off it during the day.
			</p>

			<!-- Folded away like connection size, on Jayden's call 24/09/26: the battery section was
			     carrying more than anyone reads in a meeting. Three lines, not one, when it is opened:
			     what goes in, what comes back out, and the gap between them, because the gap is the
			     round trip and it is the whole argument for or against charging at night. -->
			<div class="rsc-battdetails" *ngIf="nightCharge && nightChargedKwhPerYear !== null">
				<button type="button" class="rsc-mic-head" (click)="toggleBattDetails()"
						[attr.aria-expanded]="showBattDetails">
					<span class="rsc-mic-chev" [class.rsc-mic-chev-open]="showBattDetails">&#9656;</span>
					<span class="rsc-mic-title">Battery details</span>
				</button>

				<div class="rsc-mic-body" [class.rsc-mic-body-open]="showBattDetails">
					<div class="rsc-mic-body-inner">
						<dl class="rsc-whatif-out">
							<dt>Bought at night</dt>
							<dd>{{ nightChargedKwhPerYear | number:'1.0-0' }} {{ unit }}</dd>

							<dt>Supplied by battery</dt>
							<dd>{{ batteryDeliveredKwhPerYear | number:'1.0-0' }} {{ unit }}</dd>

							<ng-container *ngIf="roundTripLossKwhPerYear !== null">
								<dt>Lost charging and discharging</dt>
								<dd>{{ roundTripLossKwhPerYear | number:'1.0-0' }} {{ unit }}</dd>
							</ng-container>
						</dl>
					</div>
				</div>
			</div>
		</div>

		<p class="rsc-whatif-note" *ngIf="batteryUnavailableReason">
			{{ batteryUnavailableReason }}
		</p>
	</div>

	<!-- Results, back on Jayden's call 15/10/26 after the battery model trial against NREL SAM. Priced
	     only where the two models agree: solar, and a battery charged from solar. With night charging
	     on, the portal leaves out battery wear and SAM put Lee Strand's night top-up at about break
	     even, so the euro figures step aside and the units in Battery details are the answer. -->
	<div class="rsc-results" *ngIf="hasScenario">
		<h4 class="rsc-sec-title">Results</h4>

		<div class="rsc-answer" *ngIf="showPricedResults">
			<div class="rsc-answer-cell" *ngIf="totalAnnualSavingEur !== null">
				<span class="rsc-answer-value">&euro;{{ totalAnnualSavingEur | number:'1.0-0' }}</span>
				<span class="rsc-answer-label">a year</span>
			</div>
			<div class="rsc-answer-cell" *ngIf="totalSpendEur !== null && totalSpendEur > 0">
				<span class="rsc-answer-value">&euro;{{ totalSpendEur | number:'1.0-0' }}</span>
				<span class="rsc-answer-label">to spend</span>
			</div>
			<div class="rsc-answer-cell" *ngIf="simplePaybackYears !== null">
				<span class="rsc-answer-value">{{ simplePaybackYears | number:'1.1-1' }}</span>
				<span class="rsc-answer-label">years to pay back</span>
			</div>
			<div class="rsc-answer-cell" *ngIf="percentOffGrid !== null">
				<span class="rsc-answer-value">{{ percentOffGrid | number:'1.0-0' }}%</span>
				<span class="rsc-answer-label">off the grid</span>
			</div>
		</div>

		<p class="rsc-whatif-hint rsc-results-units" *ngIf="nightCharge">
			In units only while night charging is on.
		</p>
	</div>

	<!-- Tariffs. Jayden, 02/10/26: the client enters the rates they want simulated, here, rather than
	     DCS filling in a settings page per site. Folded by default like connection size. Nothing is
	     priced from an edit until Update saves it; the saved set is what everyone looking at the site
	     sees, and "Use DCS's rates" clears it again. -->
	<div class="rsc-mic rsc-rates">
		<button type="button" class="rsc-mic-head" (click)="toggleRates()" [attr.aria-expanded]="showRates">
			<span class="rsc-mic-chev" [class.rsc-mic-chev-open]="showRates">&#9656;</span>
			<span class="rsc-mic-title">Tariffs</span>
		</button>

		<div class="rsc-mic-body" [class.rsc-mic-body-open]="showRates">
			<div class="rsc-mic-body-inner">
				<div class="rsc-rate" *ngFor="let r of rateRows; let i = index" [attr.data-kind]="r.kind">
					<div class="rsc-rate-line">
						<span class="rsc-rate-name">{{ r.name }}</span>
						<span class="rsc-rate-eur">
							&euro;<input type="number" class="rsc-whatif-number rsc-rate-input"
										 min="0" max="5" step="0.0001"
										 [attr.aria-label]="r.name + ', euro per kWh'"
										 [(ngModel)]="r.eur" (ngModelChange)="onRateEdit()" />/kWh
						</span>
						<button type="button" class="rsc-rate-remove" *ngIf="r.kind !== 'day'"
								[attr.aria-label]="'Remove ' + r.name" (click)="removeRate(i)">&times;</button>
					</div>

					<div class="rsc-rate-line rsc-rate-hours" *ngIf="r.kind !== 'day'">
						<select class="rsc-whatif-select rsc-rate-hour" [attr.aria-label]="r.name + ' starts'"
								[(ngModel)]="r.fromHour" (ngModelChange)="onRateEdit()">
							<option *ngFor="let h of hourOptions" [ngValue]="h">{{ hourLabel(h) }}</option>
						</select>
						<span>to</span>
						<select class="rsc-whatif-select rsc-rate-hour" [attr.aria-label]="r.name + ' ends'"
								[(ngModel)]="r.toHour" (ngModelChange)="onRateEdit()">
							<option *ngFor="let h of hourOptions" [ngValue]="h">{{ hourLabel(h) }}</option>
						</select>
					</div>
					<p class="rsc-whatif-hint rsc-rate-note" *ngIf="r.kind === 'day'">Every hour no other rate covers.</p>
				</div>

				<div class="rsc-rate-add">
					<span>+ Add</span>
					<button type="button" class="rsc-link" *ngIf="canAddRate('night')" (click)="addRate('night')">night rate</button>
					<button type="button" class="rsc-link" *ngIf="canAddRate('evening')" (click)="addRate('evening')">evening rate</button>
					<button type="button" class="rsc-link" *ngIf="canAddRate('other')" (click)="addRate('other')">another rate</button>
				</div>

				<div class="rsc-rate-actions">
					<button type="button" class="rsc-rate-update" [disabled]="ratesSaving || !siteId" (click)="saveRates()">
						{{ ratesSaving ? 'Saving' : 'Update' }}
					</button>
					<button type="button" class="rsc-link" *ngIf="ratesAreTheirs && !ratesSaving" (click)="useDcsRates()">Use DCS's rates</button>
					<span class="rsc-rate-saved" *ngIf="ratesSaved && !ratesError">Saved</span>
				</div>
				<p class="rsc-whatif-note rsc-rate-error" *ngIf="ratesError" role="alert">{{ ratesError }}</p>
			</div>
		</div>
	</div>

	<!-- Connection charge. Shown independently of the battery toggle, because "are they paying for
	     more connection than they use" is worth answering before anyone buys anything. -->
	<div class="rsc-mic" *ngIf="showMicPanel">
		<!-- Folded by default and parked at the foot of the panel: there is nothing to do in here,
		     it is reference, so it stays out of the way until somebody wants it. -->
		<button type="button" class="rsc-mic-head" (click)="toggleMic()" [attr.aria-expanded]="showMic">
			<span class="rsc-mic-chev" [class.rsc-mic-chev-open]="showMic">&#9656;</span>
			<span class="rsc-mic-title">Connection size</span>
			<span class="rsc-info" tabindex="0"
				  (mouseenter)="showTip($event, tipMic)" (mouseleave)="hideTip()"
				  (focus)="showTip($event, tipMic)" (blur)="hideTip()">i</span>
		</button>

		<!-- Always rendered, never *ngIf: a row that does not exist cannot be animated. The grid
		     collapses from 1fr to 0fr instead, which transitions smoothly without anyone having to
		     guess a pixel height for content whose height depends on the site. -->
		<div class="rsc-mic-body" [class.rsc-mic-body-open]="showMic">
			<div class="rsc-mic-body-inner">
			<!-- Every figure here is kVA where the site meters it, because that is the unit MIC is
			     reserved and billed in. Mixing a kW demand against a kVA limit, which this panel used
			     to do, understates how close a site is to its connection size. -->
			<!-- The client's own MIC, entered here and saved against the site (Jayden, 02/10/26). Earlier
			     figures are kept in dbo.SiteMics; the newest is the one in use. -->
			<div class="rsc-mic-entry">
				<span class="rsc-mic-entry-label">MIC</span>
				<input type="number" class="rsc-whatif-number rsc-mic-input" min="1" step="1"
					   aria-label="MIC, kVA" [(ngModel)]="micEntry" (ngModelChange)="onMicEdit()"
					   (keyup.enter)="saveMic()" />
				<span>kVA</span>
				<button type="button" class="rsc-rate-update rsc-mic-update" [disabled]="micSaving || !siteId" (click)="saveMic()">
					{{ micSaving ? 'Saving' : 'Update' }}
				</button>
				<span class="rsc-rate-saved" *ngIf="micSaved && !micError">Saved</span>
			</div>
			<p class="rsc-whatif-hint rsc-mic-source" *ngIf="micIsTheirs || micKva === null">
				<ng-container *ngIf="micIsTheirs">Entered {{ micEnteredOn | date:'dd/MM/yy' }}.</ng-container>
				<ng-container *ngIf="micKva === null">Enter the MIC from the electricity bill to see how close the site runs to it.</ng-container>
			</p>
			<p class="rsc-whatif-note rsc-rate-error" *ngIf="micError" role="alert">{{ micError }}</p>

			<dl class="rsc-whatif-out">
				<dt>Highest demand</dt>
				<dd>
					{{ demandPeakKva | number:'1.0-0' }} kVA
					<span class="rsc-mic-when" *ngIf="demandPeakAt">{{ demandPeakAt | date:'d MMM y, HH:mm' }}</span>
				</dd>

				<ng-container *ngIf="micHeadroomKva !== null">
					<dt>{{ micHeadroomKva < 0 ? 'Over by' : 'Spare' }}</dt>
					<dd [class.rsc-mic-over]="micHeadroomKva < 0">
						{{ (micHeadroomKva < 0 ? -micHeadroomKva : micHeadroomKva) | number:'1.0-0' }} kVA
					</dd>
				</ng-container>

				<ng-container *ngIf="micHoursAtOrOver > 0">
					<dt>Hours at or over</dt>
					<dd class="rsc-mic-over">{{ micHoursAtOrOver | number:'1.0-0' }}</dd>
				</ng-container>

				<ng-container *ngIf="pfAtPeak !== null">
					<dt>Power factor then</dt>
					<dd>{{ pfAtPeak | number:'1.2-2' }}</dd>
				</ng-container>

				<ng-container *ngIf="peak!.shavedKw > 0">
					<dt>With the battery</dt>
					<dd>{{ micPeakAfterKva | number:'1.0-0' }} kVA</dd>

					<dt>Capacity freed</dt>
					<dd>{{ micFreedKva | number:'1.0-0' }} kVA</dd>
				</ng-container>

				<ng-container *ngIf="micSavingPerYear !== null">
					<dt>Worth per year</dt>
					<dd>€{{ micSavingPerYear | number:'1.0-0' }}</dd>
				</ng-container>
			</dl>
			</div>
		</div>

		<!-- Every note that used to sit here has gone, on Jayden's call 11/09/26: the panel plus the
		     battery table below it ran past the bottom of a Chrome window, and the notes were the
		     cheapest thing to lose. The getters behind them are deliberately left in place
		     (peakBasisNote, micReactiveNote, micLimitReason) so whatever Donal decides he wants
		     written here is a one-line re-add rather than a rewrite. -->

	</div>

</aside>

<!-- Sibling of the panel, not a child of it: the panel is a scroll box and would clip this. -->
<div class="rsc-tip" *ngIf="tip" [class.rsc-tip-above]="tipAbove"
	 [style.left.px]="tipX" [style.top.px]="tipY">{{ tip }}</div>
